/**
 * Trainer → student broadcast when the live session is paused (LCM Incomplete).
 */

export const LIVE_SESSION_PAUSED_TYPE = "codelive.session-paused" as const;

export type LiveSessionPausedMessage = {
  type: typeof LIVE_SESSION_PAUSED_TYPE;
  v: 1;
  message?: string;
  expected_resume_at?: string;
};

/** Student-facing copy when an LCM session is paused. */
export function formatLiveSessionPausedStudentMessage(
  expectedResumeAt?: string | null,
): { message: string; resumeLabel: string } {
  let resumeLabel = "";
  const raw = String(expectedResumeAt || "").trim();
  if (raw) {
    try {
      const dt = new Date(raw);
      resumeLabel = Number.isNaN(dt.getTime()) ? raw : dt.toLocaleString();
    } catch {
      resumeLabel = raw;
    }
  }
  if (resumeLabel) {
    return {
      message: `The trainer has paused this session. It will resume at ${resumeLabel}.`,
      resumeLabel,
    };
  }
  return {
    message:
      "The trainer has paused this session. Join opens after the trainer resumes.",
    resumeLabel: "",
  };
}

export function encodeLiveSessionPaused(
  msg: Omit<LiveSessionPausedMessage, "type" | "v"> = {},
): Uint8Array<ArrayBuffer> {
  const payload: LiveSessionPausedMessage = {
    type: LIVE_SESSION_PAUSED_TYPE,
    v: 1,
    ...msg,
  };
  return new TextEncoder().encode(
    JSON.stringify(payload),
  ) as Uint8Array<ArrayBuffer>;
}

export function decodeLiveSessionPaused(
  data: Uint8Array,
): LiveSessionPausedMessage | null {
  try {
    const raw = JSON.parse(new TextDecoder().decode(data)) as Partial<
      LiveSessionPausedMessage
    >;
    if (raw?.type !== LIVE_SESSION_PAUSED_TYPE || raw.v !== 1) return null;
    return {
      type: LIVE_SESSION_PAUSED_TYPE,
      v: 1,
      message:
        typeof raw.message === "string" && raw.message.trim()
          ? raw.message.trim()
          : undefined,
      expected_resume_at:
        typeof raw.expected_resume_at === "string" && raw.expected_resume_at.trim()
          ? raw.expected_resume_at.trim()
          : undefined,
    };
  } catch {
    return null;
  }
}
